import { paths } from 'src/routes/paths';

import packageJson from '../package.json';

// ----------------------------------------------------------------------

// Helper function to parse comma-separated IDs into an array of numbers
export function parseCommaSeparatedNumbers(value: string | undefined): number[] {
  if (!value) return [];
  return value
    .split(',')
    .map((id) => Number(id.trim()))
    .filter((id) => !Number.isNaN(id));
}

// Helper function to parse a number or return null
function parseNumberOrNull(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export type ConfigValue = {
  env: string;
  appName: string;
  appVersion: string;
  serverUrl: string;
  locationTileServer: string;
  assetsDir: string; // local path for local assets
  bucketUrl: string; // DO bucket url
  publicGoUrl: string; // go.amusehq.com or go.amuseapp.com for viewing live content
  communityOrgGUID: string;
  amuseSupportGUID: string; // Creator GUID for Amuse Support
  amuseCreatorGUID: string; // Creator GUID for Amuse Creator
  placeTypeSelectorDefaultID: number | null;
  langgraph: {
    url: string;
    assistantId: string;
    apiKey: string;
  };
  auth: {
    method: 'firebase';
    skip: boolean;
    redirectPath: string;
  };
  mapboxApiKey: string;
  mapboxStyle: string;
  firebase: {
    appId: string;
    apiKey: string;
    projectId: string;
    authDomain: string;
    storageBucket: string;
    measurementId: string;
    messagingSenderId: string;
  };
};

// ----------------------------------------------------------------------

export const CONFIG: ConfigValue = {
  env: import.meta.env.VITE_APP_ENV ?? '',
  appName: 'Amuse CMS',
  appVersion: packageJson.version,
  serverUrl: import.meta.env.VITE_SERVER_URL,
  locationTileServer: import.meta.env.VITE_LOCATION_TILESERVER_URL,
  assetsDir: import.meta.env.VITE_ASSETS_DIR ?? '',
  bucketUrl: import.meta.env.VITE_BUCKET_URL,
  publicGoUrl: import.meta.env.VITE_PUBLIC_GO_URL,
  communityOrgGUID: import.meta.env.VITE_COMMUNITY_ORG_GUID,
  amuseSupportGUID: import.meta.env.VITE_AMUSE_SUPPORT_GUID,
  amuseCreatorGUID: import.meta.env.VITE_AMUSE_CREATOR_GUID,
  placeTypeSelectorDefaultID: parseNumberOrNull(
    import.meta.env.VITE_PLACE_TYPE_SELECTOR_DEFAULT_ID
  ),
  /**
   * LangGraph
   */
  langgraph: {
    url: import.meta.env.VITE_LANGGRAPH_URL ?? 'http://localhost:2024',
    assistantId: import.meta.env.VITE_LANGGRAPH_ASSISTANT_ID ?? 'AtlasChat',
    apiKey: import.meta.env.VITE_LANGGRAPH_API_KEY ?? '',
  },
  /**
   * Auth
   * @method firebase
   */
  auth: {
    method: 'firebase',
    skip: false,
    redirectPath: paths.dashboard.root,
  },
  /**
   * Mapbox
   */
  mapboxApiKey: import.meta.env.VITE_MAPBOX_API_KEY ?? '',
  mapboxStyle: import.meta.env.VITE_MAPBOX_STYLE ?? '',
  /**
   * Firebase
   */
  firebase: {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
    appId: import.meta.env.VITE_FIREBASE_APPID ?? '',
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID ?? '',
  },
};
